import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, InputNumber, Spin, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import designServices from '../../../../services/oneButtonServices/designServices';
import { COMMON_CONSTANTS, useOneButtonStore } from '../../../../store/oneButtonStore';
import { US_STATE_FALLBACK } from './goodLeapShareFormOptions';
import { OBDSelect } from './form-elements';
import './oneButton-css/ContactManageModal.css';
import './oneButton-css/OneButtonFormControls.css';
import './oneButton-css/RoofingCalculatorModal.css';

type CascadingOutputValue = { CellNumber: string; CellValue: string };

function asInterestRecord(v: unknown): Record<string, boolean | undefined> {
    if (!v || typeof v !== 'object') return {};
    return v as Record<string, boolean | undefined>;
}

/** RetrievePriceQuote may include `OpportunityInterest`; opportunity detail wins when both exist (Angular uses master opp + quote). */
export function mergeOpportunityInterest(priceQuote?: any, opportunityDetails?: any) {
    return {
        ...asInterestRecord(priceQuote?.OpportunityInterest ?? priceQuote?.opportunityInterest),
        ...asInterestRecord(opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest),
    };
}

function strTrim(v: unknown): string {
    return String(v ?? '').trim();
}

function firstNonEmpty(...vals: unknown[]): string {
    for (const v of vals) {
        const s = strTrim(v);
        if (s) return s;
    }
    return '';
}

/**
 * Angular `roof-calculator-tab.component.ts`: `this.masterService.opportunityInformation?.OpportunityDetails?.Name`.
 * React store / `OpportunityDetail` payloads may nest details, use flat `Name`, or only expose the primary row in `Customers`.
 */
export function getRoofCalculatorCustomerName(opportunityDetails?: any, priceQuote?: any): string {
    const od = opportunityDetails && typeof opportunityDetails === 'object' ? opportunityDetails : {};
    const pq = priceQuote && typeof priceQuote === 'object' ? priceQuote : {};
    const nested =
        (od as any).OpportunityDetails ??
        (od as any).opportunityDetails ??
        (od as any).Opportunityinfo?.OpportunityDetails ??
        (od as any).OpportunityInfo?.OpportunityDetails;

    const fromNested = firstNonEmpty(
        nested?.Name,
        nested?.name,
        nested?.Customer_Name,
        nested?.CustomerName,
        nested?.Opportunity_Name,
        nested?.OpportunityName,
    );
    if (fromNested) return fromNested;

    const fromRoot = firstNonEmpty(
        (od as any).Name,
        (od as any).name,
        (od as any).CustomerName,
        (od as any).customerName,
        (od as any).OpportunityName,
        (od as any).Opportunity_Name,
        (od as any).AccountName,
        (od as any).accountName,
    );
    if (fromRoot) return fromRoot;

    const ci = (od as any).CustomerInformation ?? pq.CustomerInformation ?? pq.customerInformation;
    const fromCi = firstNonEmpty(ci?.Name, ci?.LegalName, ci?.legalName);
    if (fromCi) return fromCi;

    const customers = Array.isArray((od as any).Customers) ? (od as any).Customers : [];
    const primary =
        customers.find((c: any) => c?.IsPrimaryContact || c?.isPrimaryContact) ?? customers[0];
    if (primary && typeof primary === 'object') {
        const legal = strTrim(
            (primary as any).Legal_Name ??
                (primary as any).LegalName ??
                (primary as any).legal_Name ??
                (primary as any).Legal_Name__c,
        );
        if (legal) return legal;
        const parts = [
            strTrim((primary as any).FirstName ?? (primary as any).firstName),
            strTrim((primary as any).MiddleName ?? (primary as any).middleName ?? (primary as any).MI),
            strTrim((primary as any).LastName ?? (primary as any).lastName),
        ].filter(Boolean);
        if (parts.length) return parts.join(' ');
        const contact = strTrim(
            (primary as any).Name ?? (primary as any).ContactName ?? (primary as any).FullName ?? (primary as any).DisplayName,
        );
        if (contact) return contact;
    }

    return '';
}

function getOut(cellNumber: string, out: CascadingOutputValue[] | null | undefined): string | null {
    if (!out) return null;
    const hit = out.find((o) => String(o.CellNumber).trim() === String(cellNumber).trim());
    return hit?.CellValue ?? null;
}

function toNumber(v: unknown, fallback = 0): number {
    const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : fallback;
}

/** Matches Angular `(Number(getValue(...))) ? Number(...) : 0` for Excel outputs */
function excelTruthyNumber(raw: string | null | undefined): number {
    const n = Number(String(raw ?? '').replace(/,/g, ''));
    return n && !Number.isNaN(n) ? n : 0;
}

const SERVICE_FINANCE_PARTNER_ID = Number(import.meta.env.VITE_ONEBUTTON_SERVICE_FINANCE_PARTNER_ID ?? 28);

function buildRoofAddersInitial(priceQuote: any, opportunityDetails: any): AddersRow[] {
    const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest ?? {};
    let adders = DEFAULT_ROOF_ADDERS.map((a) => ({ ...a }));
    if (interest?.RRR) {
        const qty = toNumber(priceQuote?.RR_System_Size, 0) * 1000;
        adders.unshift({ default: true, type: 'RRR', quantity: qty, unitAmount: 0.65, totalAmount: qty * 0.65 });
    }
    return adders;
}

/**
 * Excel finance product code — matches Angular `openRoofingCalculator` / `getProduct()`:
 * uses dashboard `productType` (SR, SBR, BR, R) like `GenerateQuoteComponent.productType`, not quote row labels such as "Solar".
 */
function roofFinanceProductFromQuote(
    priceQuote: any,
    opportunityDetails: any,
    dashboardProductType?: string | null,
): 'R' | 'SR' | 'SBR' | 'RRR' {
    const od = opportunityDetails ?? {};
    const pq = priceQuote ?? {};
    const interest = od?.OpportunityInterest ?? od?.opportunityInterest ?? {};
    if (interest?.RRR) return 'RRR';

    const merged = mergeOpportunityInterest(priceQuote, opportunityDetails);
    const ptRaw = String(dashboardProductType ?? pq.ProductType ?? pq.productType ?? od.ProductType ?? '').trim();
    const u = ptRaw.toUpperCase();
    if (u === 'BR') return 'R';
    if (u === 'SR' || u === 'SBR' || u === 'RRR' || u === 'R') return u as 'R' | 'SR' | 'SBR' | 'RRR';

    if (merged.Battery && (merged.Roof || merged.RRR) && !merged.RoofOnly) return 'SBR';
    if (merged.Solar && merged.Roof && !merged.RoofOnly && !merged.RRR) return 'SR';
    if (merged.RoofOnly) return 'R';
    return 'R';
}

function normStr(v: unknown): string {
    return String(v ?? '')
        .trim()
        .toLowerCase();
}

/** GoodLeap spelling variants → display string used in grids / Excel (matches `RoofQuoteDetails.excelPartnerName`). */
function roofPartnerLabelForExcel(name: string): string {
    const p = String(name ?? '').trim();
    if (p === 'GoodLeap' || p === 'Goodleep') return 'Good Leap';
    return p;
}

/** When `Battery.Finance_Method_Name` is blank, use `QuoteVersion` (Angular `batteryOnly.contract_Type`). */
function batteryFinancingMethodFromQuoteVersions(pq: any, batteryFinanced: string | null | undefined): string | null {
    const versions = pq?.QuoteVersion;
    if (!Array.isArray(versions)) return null;
    let rows = versions.filter((v: any) => /battery/i.test(String(v?.ProductType ?? v?.productType ?? '')));
    if (batteryFinanced) {
        const want = roofPartnerLabelForExcel(String(batteryFinanced).trim());
        const narrowed = rows.filter((v: any) => {
            const fp = roofPartnerLabelForExcel(String(v?.FinancePartner ?? '').trim());
            if (!fp) return false;
            return normStr(fp) === normStr(want) || fp === want;
        });
        if (narrowed.length > 0) rows = narrowed;
    }
    if (rows.length === 0) return null;
    const sel =
        rows.find((v: any) => v?.Selected === true || v?.selected === true || v?.isSelectedCO === true) ?? rows[0];
    return String(sel?.Quote_FinanceMethod ?? sel?.QuoteFinanceMethod ?? '').trim() || null;
}

function mapFinancePartnerIdToName(
    id: unknown,
    financePartnersList: Array<{ Text?: string; Value?: string | number }> | undefined,
): string | null {
    if (id == null || id === '' || Number(id) === 0) return null;
    if (!financePartnersList?.length) return null;
    const idStr = String(id).trim();
    const row = financePartnersList.find((x) => {
        const v = x.Value ?? (x as { value?: string | number }).value;
        return v != null && String(v).trim() === idStr;
    });
    const t = String(row?.Text ?? (row as { text?: string })?.text ?? '').trim();
    return t || null;
}

/** Prefer active opportunity row, then any row for that product type (matches common API shapes). */
function findOpportunityProduct(products: any, productTypeName: string): any | undefined {
    const arr = Array.isArray(products) ? products : [];
    const name = String(productTypeName).trim();
    const active = arr.find((p: any) => p?.ActiveFlag && String(p?.Product_Type_Name ?? '').trim() === name);
    if (active) return active;
    return arr.find((p: any) => String(p?.Product_Type_Name ?? '').trim() === name);
}

function isRoofQuoteActive(pq: any): boolean {
    const rq = pq?.RoofQuote ?? {};
    return rq.RoofPackage === true || rq.RoofPackage === 'true' || rq.roofPackage === true;
}

/** Roof line method from `RetrievePriceQuote` — API often puts display method in `Finance_Method_Name` while `ContractType` is null. */
function roofMethodFromRoofQuoteBlock(pq: any): string | null {
    if (!isRoofQuoteActive(pq)) return null;
    const rq = pq?.RoofQuote ?? {};
    const ct = String(rq.ContractType ?? rq.contractType ?? '').trim();
    if (ct) return ct;
    const fm = String(rq.Finance_Method_Name ?? rq.finance_Method_Name ?? rq.FinanceMethodName ?? '').trim();
    return fm || null;
}

/**
 * When roof is Cash (or similar), `FinancePartnerId` may still match a shared id (e.g. Good Leap on solar).
 * Prefer `QuoteVersion.FinancePartner` on a row whose finance method matches the roof line.
 */
function roofPartnerFromRoofQuoteAndVersions(
    pq: any,
    financePartnersList: Array<{ Text?: string; Value?: string | number }> | undefined,
): string | null {
    if (!isRoofQuoteActive(pq)) return null;
    const rq = pq?.RoofQuote ?? {};
    const roofMethod = roofMethodFromRoofQuoteBlock(pq);
    const lowerMethod = roofMethod ? roofMethod.toLowerCase() : '';

    if (lowerMethod.includes('cash')) {
        return 'Cash';
    }

    const versions = pq?.QuoteVersion;
    if (Array.isArray(versions) && roofMethod) {
        const hit = versions.find((v: any) => {
            const qfm = String(v?.Quote_FinanceMethod ?? v?.QuoteFinanceMethod ?? '').trim();
            const pm = String(v?.PricingMethod ?? '').trim();
            if (qfm && normStr(qfm) === normStr(roofMethod)) return true;
            if (pm && pm.toLowerCase().includes(lowerMethod)) return true;
            if (qfm && qfm.toLowerCase().includes(lowerMethod)) return true;
            return false;
        });
        const fp = String(hit?.FinancePartner ?? '').trim();
        if (fp) return roofPartnerLabelForExcel(fp);
    }

    return mapFinancePartnerIdToName(rq.FinancePartnerId ?? rq.financePartnerId, financePartnersList);
}

/**
 * Roof financing method: saved quote fields → roof UI (`roofDetails`) → **RoofQuote row** (RetrievePriceQuote)
 * before solar line — solar `Finance_Method_Name` is the PV product, not the roof adder when `RoofQuote.Finance_Method_Name` is set.
 */
function roofFinancingMethodFromQuote(pq: any, od: any, roofDetails?: any | null): string | null {
    const interest = od?.OpportunityInterest ?? od?.opportunityInterest ?? {};
    const saved = String(pq?.RoofFinanceMethod ?? pq?.roofFinanceMethod ?? '').trim();
    if (saved) return saved;

    const rd = String(roofDetails?.contract_Type ?? '').trim();
    if (rd) return rd;

    if (interest?.RoofOnly) {
        const pkg = pq?.RoofOnlyPackage ?? {};
        const m = String(pkg?.ContractType ?? pkg?.FinanceMethodId ?? '').trim();
        if (m && m !== '0') return m;
    }

    const fromRoofBlock = roofMethodFromRoofQuoteBlock(pq);
    if (fromRoofBlock) return fromRoofBlock;

    const solar = pq?.Solar ?? {};
    if (!interest?.RoofOnly && interest?.Solar) {
        const solarCt = String(solar.ContractType ?? solar.contractType ?? '').trim();
        if (solarCt) return solarCt;
        const solarFm = String(
            solar.Finance_Method_Name ?? solar.FinanceMethodName ?? solar.finance_Method_Name ?? solar.FinanceMethod ?? '',
        ).trim();
        if (solarFm) return solarFm;
    }

    const rqCt = String(pq?.RoofQuote?.ContractType ?? pq?.RoofQuote?.contractType ?? '').trim();
    if (rqCt) return rqCt;

    const products = od?.OpportunityProduct ?? od?.opportunityProduct ?? [];
    if (!interest?.RoofOnly && interest?.Solar) {
        const solarOpp = findOpportunityProduct(products, 'Solar');
        const sm = String(solarOpp?.Finance_Method_Name ?? '').trim();
        if (sm) return sm;
    }
    const roofOpp = findOpportunityProduct(products, 'Roof');
    const oppM = String(roofOpp?.Finance_Method_Name ?? '').trim();
    return oppM || null;
}

function roofPartnerDisplayFromQuote(
    pq: any,
    od: any,
    financePartnersList: Array<{ Text?: string; Value?: string | number }> | undefined,
    roofDetails?: any | null,
): string | null {
    // Do not use `RequestedPartner` — it is sales/LOI request (e.g. Good Leap PPA), not the bound roof finance line.
    const direct = String(pq?.RoofFinancingPartner ?? pq?.roofFinancingPartner ?? '').trim();
    if (direct) return roofPartnerLabelForExcel(direct);

    const rdName = String(roofDetails?.financePartnerName ?? '').trim();
    if (rdName) return roofPartnerLabelForExcel(rdName);

    const interest = od?.OpportunityInterest ?? od?.opportunityInterest ?? {};
    const products = od?.OpportunityProduct ?? od?.opportunityProduct ?? [];
    const solar = pq?.Solar ?? {};

    if (interest?.RoofOnly) {
        const pkg = pq?.RoofOnlyPackage ?? {};
        const fromId = mapFinancePartnerIdToName(pkg?.FinancePartnerId ?? pkg?.financePartnerId, financePartnersList);
        if (fromId) return fromId;
    }

    const fromRoofLine = roofPartnerFromRoofQuoteAndVersions(pq, financePartnersList);
    if (fromRoofLine) return fromRoofLine;

    if (interest?.Solar && (interest?.Roof || interest?.RRR)) {
        const fromSolarId = mapFinancePartnerIdToName(
            solar.FinancePartnerId ?? solar.financePartnerId ?? solar.FinancePartnerID,
            financePartnersList,
        );
        if (fromSolarId) return fromSolarId;
        const solarOpp = findOpportunityProduct(products, 'Solar');
        const sn = String(
            solarOpp?.Finance_Partner_Display_Name ??
                solar.Finance_Partner_Display_Name ??
                solar.FinancePartnerDisplayName ??
                '',
        ).trim();
        if (sn) return roofPartnerLabelForExcel(sn);
    }

    const roofOpp = findOpportunityProduct(products, 'Roof');
    const fromRoofOpp = String(roofOpp?.Finance_Partner_Display_Name ?? '').trim();
    if (fromRoofOpp) return roofPartnerLabelForExcel(fromRoofOpp);

    const fromRoofQuoteIdFallback = mapFinancePartnerIdToName(
        pq?.RoofQuote?.FinancePartnerId ?? pq?.RoofQuote?.financePartnerId,
        financePartnersList,
    );
    if (fromRoofQuoteIdFallback) return fromRoofQuoteIdFallback;

    const onlyPkgId = pq?.RoofOnlyPackage?.FinancePartnerId ?? pq?.RoofOnlyPackage?.financePartnerId;
    const fromOnlyPkg = mapFinancePartnerIdToName(onlyPkgId, financePartnersList);
    if (fromOnlyPkg) return fromOnlyPkg;

    return null;
}

/** Pick dropdown value that matches `raw` with trim / case / GoodLeap spacing tolerance. */
function pickMatchingDropdownValue(raw: string | null | undefined, options: Array<{ Text?: string; Value?: string }>): string | null {
    const want = normStr(raw);
    if (!want) return null;
    const list = options || [];
    const exact = list.find((o) => normStr(o.Value) === want || normStr(o.Text) === want);
    if (exact) return String(exact.Value ?? exact.Text ?? '').trim() || null;
    const compact = (s: string) => s.replace(/\s+/g, '');
    const w2 = compact(want);
    const fuzzy = list.find((o) => compact(normStr(o.Value)) === w2 || compact(normStr(o.Text)) === w2);
    return fuzzy ? String(fuzzy.Value ?? fuzzy.Text ?? '').trim() || null : null;
}

function createRoofCalcInitial(
    priceQuote: any,
    opportunityDetails: any,
    dashboardProductType?: string | null,
    financePartnersList?: Array<{ Text?: string; Value?: string | number }>,
    roofDetails?: any | null,
): RoofCalcState {
    const pq = priceQuote ?? {};
    const od = opportunityDetails ?? {};
    const roof = pq.Roof ?? {};
    const low = toNumber(pq.Low_Slope_Override ?? pq.LowSlope ?? 0, 0);
    const normal = toNumber(pq.Normal_Slope_Override ?? pq.NormalSlope ?? 0, 0);
    const manu = String(roof.ManufacturerName ?? '').trim();
    const prod = String(roof.RoofProduct ?? roof.Roofing_product ?? '').trim();
    const roofProductCombined = manu && prod ? `${manu} ${prod}`.trim() : (prod || manu || 'IKO Dynasty');

    const products = od.OpportunityProduct ?? od.opportunityProduct ?? [];
    const roofOppProduct = findOpportunityProduct(products, 'Roof');
    const roofingPartner = roofPartnerDisplayFromQuote(pq, od, financePartnersList, roofDetails);
    const financingMethod = roofFinancingMethodFromQuote(pq, od, roofDetails);
    const financingTerm = String(pq.RoofFinanceTerm ?? '').trim() || null;

    return {
        currentDate: nowMmDdYyyy(),
        customerName: getRoofCalculatorCustomerName(od, pq),
        roofState: String(
            od.InstallationAddress?.stateCode ?? od.InstallationAddress?.StateId ?? pq.InstallationState ?? od.InstallationState ?? 'CT',
        ),
        roofingType: 'Asphalt',
        NormalSlope: normal,
        LowSlope: low,
        TotalRoofSquares: normal + low,
        ExistingShingleLayers: 1,
        RoofProduct: roofProductCombined,
        roofingPartner,
        FinancingMethod: financingMethod || null,
        FinancingTerm: financingTerm || null,
        GrossRoofingPrice: 0,
        Deposit: 0,
        Adders: buildRoofAddersInitial(pq, od),
        PricePerSqFeet: toNumber(pq.PricePerSqFeet ?? 1, 1),
        FinanceProduct: roofFinanceProductFromQuote(pq, od, dashboardProductType),
        SDDF_Lead_source: pq.SDDF_Lead_source ?? null,
        Sales_Division: pq.Sales_Division ?? null,
        Roofing_Team: pq.Roofing_Team ?? null,
        ouput: {
            DealerFee: 0,
            GrossSalesPricePerSquareFoot: 0,
            GrossSalesPrice: 0,
            MonthlyFinancePayment: 0,
            NetSalesPricePerSquareFoot: 0,
            SalesCommission: 0,
            MinimumSolarSystemSalesPrice: 0,
        },
    };
}

function createBatteryCalcInitial(priceQuote: any, opportunityDetails: any): BatteryCalcState {
    const pq = priceQuote ?? {};
    const od = opportunityDetails ?? {};
    const bi = pq.BatteryInformation ?? pq.Battery_Info ?? {};
    const bat = pq.Battery ?? {};
    const products = od.OpportunityProduct ?? od.opportunityProduct ?? [];
    const batteryOppProduct = (Array.isArray(products) ? products : []).find((p: any) => p.Product_Type_Name === 'Battery');
    const interest = mergeOpportunityInterest(pq, od);
    const batteryOnlyPkg = pq.BatteryOnlyPackage ?? {};
    let batteryFinanced =
        String(
            pq.BatteryFinancingPartner ??
                bat.Finance_Partner_Display_Name ??
                batteryOppProduct?.Finance_Partner_Display_Name ??
                '',
        ).trim() || null;
    if (interest.BatteryOnly && !batteryFinanced) {
        batteryFinanced =
            String(
                batteryOnlyPkg.Finance_Partner_Display_Name ??
                    batteryOnlyPkg.finance_Partner_Display_Name ??
                    '',
            ).trim() || null;
    }
    let financingMethod = String(bat.Finance_Method_Name ?? bat.ContractType ?? pq.BatteryFinanceMethod ?? '').trim() || null;
    if (interest.BatteryOnly && !financingMethod) {
        financingMethod = String(
            batteryOnlyPkg.finance_Method_Name ?? batteryOnlyPkg.Finance_Method_Name ?? batteryOnlyPkg.contract_Type ?? batteryOnlyPkg.ContractType ?? '',
        ).trim() || null;
    }
    if (!financingMethod) {
        financingMethod = batteryFinancingMethodFromQuoteVersions(pq, batteryFinanced);
    }
    const termAPR = String(pq.BatteryFinanceTerm ?? '').trim() || null;
    /** Angular `openRoofingCalculator`: `CommissionTier: this.batteryOnly?.priceTier` — battery-only uses package / quote tier fields. */
    let commissionRaw = '';
    if (interest.BatteryOnly) {
        commissionRaw = String(
            batteryOnlyPkg.priceTier ??
                batteryOnlyPkg.PriceTier ??
                batteryOnlyPkg.PricingMethod ??
                batteryOnlyPkg.pricing_Type ??
                pq.PriceTier ??
                pq.priceTier ??
                '',
        ).trim();
    }
    if (!commissionRaw) {
        commissionRaw = String(bat.PricingMethod ?? bat.pricing_Type ?? pq.BatteryCommissionTier ?? 'Standard').trim();
    }
    const tier = (['Floor', 'Min', 'Mid', 'Standard'].includes(commissionRaw) ? commissionRaw : 'Standard') as BatteryCalcState['CommissionTier'];

    return {
        currentDate: nowMmDdYyyy(),
        customerName: getRoofCalculatorCustomerName(od, pq),
        batteryState: String(od.InstallationAddress?.stateCode ?? od.InstallationAddress?.StateId ?? pq.InstallationState ?? od.InstallationState ?? 'CT'),
        batteryFinanced,
        FinancingMethod: financingMethod || null,
        batteryManufacturer: String(bi.Manufacturer ?? bi.ManufacturerName ?? '').trim() || null,
        BatteryProduct: String(bi.ModelName ?? bi.BatteryModelName ?? '').trim() || null,
        termAPR: termAPR || null,
        batteryCount: String(bi.Quantity ?? bat.Quantity ?? '1'),
        CommissionTier: tier,
        batteryFireEnclosure: false,
        luminSmartPanel: false,
        lockoutPanel: false,
        ancillaryAdderCost: 0,
        ouput: { DealerFee: 0, BatteryGrossSalesPrice: 0, MonthlyFinancePaymentByCustomer: 0, SalesCommission: 0 },
    };
}

function nowMmDdYyyy(): string {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
}

type StateOption = { Text: string; Value: string };

function titleCaseToken(word: string): string {
    const w = String(word ?? '').trim();
    if (!w) return w;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

/** Same display shaping as `ContactManageModal` state labels. */
function formatStateDisplayName(rawLabel: string): string {
    return String(rawLabel ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(titleCaseToken)
        .join(' ');
}

const US_STATE_BY_CODE = new Map(US_STATE_FALLBACK.map((s) => [s.value.toUpperCase(), s]));
const US_STATE_LABEL_COMPACT = new Map(
    US_STATE_FALLBACK.map((s) => [s.label.replace(/\s+/g, '').toUpperCase(), s.value] as const),
);

/**
 * `StatesFullName` often uses numeric `Id` or full name as `Value`, while install / quote use 2-letter codes.
 * Ant Design Select must use **abbreviation as option `value`** so `roofState === "PA"` matches and the **label** shows in the closed field (Angular nz-select behavior).
 */
function resolveUsStateOptionCode(valueRaw: string, labelRaw: string): string {
    const v = String(valueRaw ?? '').trim();
    const lab = String(labelRaw ?? '').trim();
    if (/^[a-z]{2}$/i.test(v) && US_STATE_BY_CODE.has(v.toUpperCase())) {
        return v.toUpperCase();
    }
    const tryLabel = (s: string) => {
        const c = s.replace(/\s+/g, '').toUpperCase();
        return US_STATE_LABEL_COMPACT.get(c) ?? null;
    };
    const fromLab = tryLabel(lab);
    if (fromLab) return fromLab;
    const fromVal = tryLabel(v);
    if (fromVal) return fromVal;
    const low = lab.toLowerCase();
    const hit = US_STATE_FALLBACK.find((s) => s.label.toLowerCase() === low);
    if (hit) return hit.value;
    return v || lab;
}

/**
 * Roof/battery calculator Customer state dropdown: prefer `StatesFullName` (full names in UI, codes in `Value`)
 * like `opportunity-info` / `ContactManageModal`. Fall back to `stateCodeandName` (Angular generate-quote) and
 * expand abbreviations via `US_STATE_FALLBACK` when labels are only two-letter codes.
 */
function stateOptionsFromOpportunityMasterData(data: any | null | undefined): StateOption[] {
    if (!data) return [];

    const statesFull = data.StatesFullName ?? data.statesFullName;
    if (Array.isArray(statesFull) && statesFull.length > 0) {
        const seen = new Set<string>();
        const out: StateOption[] = [];
        for (const item of statesFull) {
            const o = item as Record<string, unknown>;
            const rawLabel = (o.Text ?? o.text ?? o.Label ?? o.label ?? '') as string;
            const rawValue =
                o.Value !== undefined && o.Value !== null
                    ? String(o.Value)
                    : o.Id !== undefined && o.Id !== null
                      ? String(o.Id)
                      : String(rawLabel || '').trim();
            const labelSource = String(rawLabel || rawValue).trim();
            if (!labelSource && !rawValue) continue;
            const code = resolveUsStateOptionCode(rawValue, labelSource).toUpperCase();
            if (!US_STATE_BY_CODE.has(code)) continue;
            if (seen.has(code)) continue;
            seen.add(code);
            const label = formatStateDisplayName(labelSource) || US_STATE_BY_CODE.get(code)?.label || code;
            out.push({ Value: code, Text: label });
        }
        return out;
    }

    const codeToFull = new Map(US_STATE_FALLBACK.map((s) => [s.value.toUpperCase(), s.label]));
    const statesRaw = (data.stateCodeandName ||
        data.StateList ||
        data.State ||
        data.States ||
        data.stateList) as unknown;
    if (!Array.isArray(statesRaw) || statesRaw.length === 0) return [];

    const seenFb = new Set<string>();
    const outFb: StateOption[] = [];
    for (const s of statesRaw) {
        let value = String((s as any)?.Value ?? (s as any)?.value ?? (s as any)?.StateCode ?? (s as any)?.stateCode ?? '').trim();
        let text = String((s as any)?.Text ?? (s as any)?.text ?? (s as any)?.StateName ?? (s as any)?.stateName ?? '').trim();
        if (!value && text) value = text;
        if (!text && value && /^[a-z]{2}$/i.test(value)) {
            text = codeToFull.get(value.toUpperCase()) ?? value;
        }
        const abbrev = value.length === 2 ? value.toUpperCase() : '';
        const textLooksLikeAbbrev = /^[a-z]{2}$/i.test(text);
        if (abbrev && textLooksLikeAbbrev && text.toUpperCase() === abbrev) {
            const full = codeToFull.get(abbrev);
            if (full) text = full;
        } else if (text) {
            text = formatStateDisplayName(text) || text;
        }
        const code = resolveUsStateOptionCode(value || abbrev, text).toUpperCase();
        if (!US_STATE_BY_CODE.has(code)) continue;
        if (seenFb.has(code)) continue;
        seenFb.add(code);
        const label =
            formatStateDisplayName(text) || US_STATE_BY_CODE.get(code)?.label || codeToFull.get(code) || code;
        outFb.push({ Value: code, Text: label });
    }
    return outFb;
}

type AddersRow = { default: boolean; type: string; quantity: number; unitAmount: number; totalAmount: number; canRemove?: boolean };

// Alias used in helper signatures (kept separate for readability)
type RoofAdder = AddersRow;

const DEFAULT_ROOF_ADDERS: AddersRow[] = [
    { default: true, type: 'Skylights (Fixed)', quantity: 0, unitAmount: 2000.0, totalAmount: 0 },
    { default: true, type: 'Skylights (Operable)', quantity: 0, unitAmount: 2000.0, totalAmount: 0 },
    { default: true, type: 'Skylight Curb Kits', quantity: 0, unitAmount: 295.0, totalAmount: 0 },
    { default: true, type: 'Plywood', quantity: 0, unitAmount: 100.0, totalAmount: 0 },
    { default: false, type: '', quantity: 0, unitAmount: 0, totalAmount: 0 },
];

type RoofOutput = {
    DealerFee: number;
    GrossSalesPricePerSquareFoot: number;
    GrossSalesPrice: number;
    MonthlyFinancePayment: number;
    NetSalesPricePerSquareFoot: number | string;
    SalesCommission: number;
    MinimumSolarSystemSalesPrice: number;
};

type RoofCalcState = {
    currentDate: string;
    customerName: string;
    roofState: string;
    roofingType: string;
    NormalSlope: number;
    LowSlope: number;
    TotalRoofSquares: number;
    ExistingShingleLayers: number;
    RoofProduct: string;
    roofingPartner: string | null;
    FinancingMethod: string | null;
    FinancingTerm: string | null;
    GrossRoofingPrice: number;
    Deposit: number;
    Adders: AddersRow[];
    PricePerSqFeet: number;
    FinanceProduct: 'R' | 'SR' | 'SBR' | 'RRR';
    SDDF_Lead_source: string | null;
    Sales_Division: string | null;
    Roofing_Team: string | null;
    ouput: RoofOutput;
};

type BatteryOutput = {
    DealerFee: number;
    BatteryGrossSalesPrice: number;
    MonthlyFinancePaymentByCustomer: number;
    SalesCommission: number;
};

type BatteryCalcState = {
    currentDate: string;
    customerName: string;
    batteryState: string;
    batteryFinanced: string | null;
    FinancingMethod: string | null;
    batteryManufacturer: string | null;
    BatteryProduct: string | null;
    termAPR: string | null;
    batteryCount: string;
    CommissionTier: 'Floor' | 'Min' | 'Mid' | 'Standard';
    batteryFireEnclosure: boolean;
    luminSmartPanel: boolean;
    lockoutPanel: boolean;
    ancillaryAdderCost: number;
    ouput: BatteryOutput;
};

export interface RoofingCalculatorModalProps {
    priceQuote?: any;
    opportunityDetails?: any;
    /** Optional; parent Modal typically supplies its own close control. */
    onRequestClose?: () => void;
}

const PRODUCT_LIST = [
    { Text: 'Roof only', Value: 'R' },
    { Text: 'Solar Roof', Value: 'SR' },
    { Text: 'Solar Battery Roof', Value: 'SBR' },
    { Text: 'RRR', Value: 'RRR' },
] as const;

function fmtCurrency(n: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number.isFinite(n) ? n : 0);
}

function fmtPercent(n: number): string {
    const x = Number.isFinite(n) ? n : 0;
    return `${(x * 100).toFixed(2)}%`;
}

type UsdFormatInfo = { userTyping: boolean; input: string };

/** `$` prefix for amount `InputNumber`s (Angular `currencyMask` with `prefix: '$'`). Squares/quantities stay plain. */
function usdAmountFormatter(value: number | string | null | undefined, info: UsdFormatInfo): string {
    if (value === undefined || value === null || value === '') return '';
    if (info.userTyping) {
        const raw = (info.input ?? '').replace(/[^0-9.]/g, '') || String(value).replace(/[^0-9.]/g, '');
        if (raw === '') return '$';
        return raw.startsWith('.') ? `$.${raw.slice(1)}` : `$${raw}`;
    }
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(n)) return '';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
}

function usdAmountParser(displayValue: string | undefined): number {
    const s = String(displayValue ?? '').replace(/[$,\s]/g, '');
    if (s === '' || s === '.') return 0;
    const n = Number(s);
    return Number.isNaN(n) ? 0 : n;
}

/** Angular `roof-calculator-tab` / `battery-calculator-tab` `onAmountFieldSelect`: select entire value on focus for amount fields */
function selectAllAmountInputOnFocus(e: React.FocusEvent<HTMLInputElement>) {
    const el = e.target;
    if (el && typeof el.select === 'function') {
        window.requestAnimationFrame(() => {
            try {
                el.select();
            } catch {
                /* ignore */
            }
        });
    }
}

/**
 * Angular `generate-quote.component.html` Roofing Calculator button *ngIf* and
 * `roofing-calculator-modal.component.html`: Roof tab iff `data.RoofDetails`, Battery tab iff `data.BatteryDetails`,
 * which `openRoofingCalculator()` sets only from `OpportunityInterest` (Roof / RoofOnly / RRR and Battery / BatteryOnly).
 */
export function getRoofingCalculatorTabVisibility(priceQuote?: any, opportunityDetails?: any) {
    const merged = mergeOpportunityInterest(priceQuote, opportunityDetails);
    const showRoof = !!(merged.Roof || merged.RoofOnly || merged.RRR);
    const showBattery = !!(merged.Battery || merged.BatteryOnly);
    return { showRoof, showBattery };
}

/** Same gate as Angular “Roofing Calculator” action: any of roof / roof-only / RRR / battery / battery-only interest. */
export function shouldShowRoofSolarCalculatorMenu(priceQuote?: any, opportunityDetails?: any): boolean {
    const { showRoof, showBattery } = getRoofingCalculatorTabVisibility(priceQuote, opportunityDetails);
    return showRoof || showBattery;
}

const RoofingCalculatorModal: React.FC<RoofingCalculatorModalProps> = ({ priceQuote, opportunityDetails }) => {
    const theme = useOneButtonStore((s) => s.theme);
    const dashboardProductType = useOneButtonStore((s) => s.productType);
    const { showRoof, showBattery } = useMemo(
        () => getRoofingCalculatorTabVisibility(priceQuote, opportunityDetails),
        [priceQuote, opportunityDetails],
    );

    /** Angular `nz-tabset`: Roof first when both exist. */
    const calculatorDefaultTab = useMemo(() => (showRoof ? 'roof' : 'battery'), [showRoof, showBattery]);

    const [loadingCount, setLoadingCount] = useState(0);
    const incLoading = useCallback(() => setLoadingCount((c) => c + 1), []);
    const decLoading = useCallback(() => setLoadingCount((c) => Math.max(0, c - 1)), []);

    /** Portal to body + high z-index (expanded quotes / stacked modals). `virtual: false` avoids blank option lists in some layouts. */
    const modalSelectDropdown = useMemo(
        () => ({
            getPopupContainer: () => document.body,
            styles: { popup: { root: { zIndex: 200000 } } },
            virtual: false,
            listHeight: 320,
        }),
        [],
    );

    const selectPopupClass = useMemo(() => `obd-select-dropdown obd-roofing-calc-select-dd obd-theme-${theme}`, [theme]);

    const opportunityinfoMasterData = useOneButtonStore((s) => s.opportunityinfoMasterData);
    const financePartnersList = useOneButtonStore((s) => s.financePartnersList) as Array<{ Text?: string; Value?: string | number }>;
    const roofDetails = useOneButtonStore((s) => s.roofDetails);
    const roofDetailsSyncKey = useMemo(() => {
        if (!roofDetails || typeof roofDetails !== 'object') return '';
        return [
            roofDetails.financePartnerName,
            roofDetails.contract_Type,
            roofDetails.TermAPR,
            roofDetails.TermAPRArr,
        ]
            .map((x) => String(x ?? '').trim())
            .join('|');
    }, [roofDetails]);
    const [stateList, setStateList] = useState<StateOption[]>([]);

    const [roofCalc, setRoofCalc] = useState<RoofCalcState>(() =>
        createRoofCalcInitial(priceQuote, opportunityDetails, dashboardProductType, financePartnersList, roofDetails),
    );

    useEffect(() => {
        setRoofCalc(createRoofCalcInitial(priceQuote, opportunityDetails, dashboardProductType, financePartnersList, roofDetails));
    }, [priceQuote, opportunityDetails, dashboardProductType, financePartnersList, roofDetailsSyncKey]);

    useEffect(() => {
        const mapped = stateOptionsFromOpportunityMasterData(opportunityinfoMasterData);
        if (mapped.length > 0) setStateList(mapped);
    }, [opportunityinfoMasterData]);

    useEffect(() => {
        if (opportunityinfoMasterData) return;
        let mounted = true;
        masterServices
            .GetMasterDataByStep({ step: 3, paramDTOs: [{ key: 'string', value: 'string' }] })
            .then((res: any) => {
                if (!mounted) return;
                const data = res?.data || res;
                const mapped = stateOptionsFromOpportunityMasterData(data);
                if (mapped.length > 0) setStateList(mapped);
            })
            .catch(() => {
                // If master fails we keep stateList empty (dropdown shows loading placeholder).
            });
        return () => {
            mounted = false;
        };
    }, [opportunityinfoMasterData]);

    const customerName = useMemo(
        () => getRoofCalculatorCustomerName(opportunityDetails, priceQuote),
        [opportunityDetails, priceQuote],
    );

    const installState = useMemo(() => {
        return (
            opportunityDetails?.InstallationAddress?.stateCode ??
            opportunityDetails?.InstallationAddress?.StateId ??
            priceQuote?.InstallationState ??
            opportunityDetails?.InstallationState ??
            'CT'
        );
    }, [priceQuote, opportunityDetails]);

    const normalizeStateValue = useCallback(
        (val: any, list: StateOption[]) => {
            const raw = String(val ?? '').trim();
            if (!raw || list.length === 0) return raw || 'CT';
            const direct = list.find((s) => String(s.Value).toLowerCase() === raw.toLowerCase());
            if (direct) return direct.Value;
            const byText = list.find((s) => String(s.Text).toLowerCase() === raw.toLowerCase());
            return byText?.Value ?? raw;
        },
        [],
    );

    useEffect(() => {
        const name = String(customerName ?? '').trim();
        if (!name) return;
        setRoofCalc((s) => (s.customerName === name ? s : { ...s, customerName: name }));
        setBatteryCalc((s) => (s.customerName === name ? s : { ...s, customerName: name }));
    }, [customerName]);

    useEffect(() => {
        if (stateList.length === 0) return;
        setRoofCalc((s) => {
            const next = normalizeStateValue(s.roofState, stateList);
            return next === s.roofState ? s : { ...s, roofState: next };
        });
        setBatteryCalc((s) => {
            const next = normalizeStateValue(s.batteryState, stateList);
            return next === s.batteryState ? s : { ...s, batteryState: next };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateList]);

    // keep derived totals consistent
    useEffect(() => {
        setRoofCalc((s) => {
            const total = toNumber(s.NormalSlope, 0) + toNumber(s.LowSlope, 0);
            if (s.TotalRoofSquares === total) return s;
            return { ...s, TotalRoofSquares: total };
        });
    }, [roofCalc.NormalSlope, roofCalc.LowSlope]); // eslint-disable-line react-hooks/exhaustive-deps

    const [roofProductList, setRoofProductList] = useState<string[]>([]);
    const [roofingPartners, setRoofingPartners] = useState<Array<{ Text: string; Value: string }>>([]);
    const [roofingMethods, setRoofingMethods] = useState<Array<{ Text: string; Value: string }>>([]);
    const [roofingTerms, setRoofingTerms] = useState<Array<{ Text: string; Value: string }>>([]);

    const getRoofProductCode = useCallback((financeProduct: string): string => {
        let product = financeProduct;
        if (financeProduct === 'RRR' || financeProduct === 'BR') product = 'R';
        return product;
    }, []);

    const loadRoofProductsAndPartners = useCallback(async () => {
        const roofState = String(roofCalc.roofState || 'CT').trim();
        const productCode = getRoofProductCode(roofCalc.FinanceProduct);
        setRoofProductList([]);
        setRoofingPartners([]);
        incLoading();
        try {
            const out = (await masterServices.cascadingDropDown({
                ExcelFileName: 'Roofing Calculator',
                SheetName: 'Roof Pricing',
                InputValues: [
                    { CellNumber: ' C3', CellValue: roofState, Type: 'string' },
                    { CellNumber: 'C4', CellValue: productCode, Type: 'string' },
                    { CellNumber: 'C5', CellValue: 'Roofing', Type: 'string' },
                ],
                OutputCells: ['E3', 'T3'],
            })) as CascadingOutputValue[] | null;

            const manufacturers = String(out?.[0]?.CellValue ?? '').split('|').map((s) => s.trim()).filter(Boolean);
            const partners = String(out?.[1]?.CellValue ?? '')
                .split('|')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((p) => ({ Text: p, Value: p }));
            setRoofingPartners(partners);

            setRoofCalc((s) => {
                if (!s.roofingPartner || partners.length === 0) return s;
                const matched = pickMatchingDropdownValue(s.roofingPartner, partners);
                return matched && matched !== s.roofingPartner ? { ...s, roofingPartner: matched } : s;
            });

            // Angular: nested subscribe per manufacturer (sequential; preserves product order)
            const all: string[] = [];
            for (const m of manufacturers) {
                incLoading();
                try {
                    const out2 = (await masterServices.cascadingDropDown({
                        ExcelFileName: 'Roofing Calculator',
                        SheetName: 'Roof Pricing',
                        InputValues: [
                            { CellNumber: ' C3', CellValue: roofState, Type: 'string' },
                            { CellNumber: 'C4', CellValue: productCode, Type: 'string' },
                            { CellNumber: 'C5', CellValue: 'Roofing', Type: 'string' },
                            { CellNumber: 'C6', CellValue: m, Type: 'string' },
                        ],
                        OutputCells: ['E4'],
                    })) as CascadingOutputValue[] | null;
                    const products = String(out2?.[0]?.CellValue ?? '')
                        .split('|')
                        .map((s) => s.trim())
                        .filter(Boolean);
                    products.forEach((p) => all.push(`${m} ${p}`));
                } finally {
                    decLoading();
                }
            }
            setRoofProductList(all);
        } finally {
            decLoading();
        }
    }, [roofCalc.roofState, roofCalc.FinanceProduct, getRoofProductCode, incLoading, decLoading]);

    const loadRoofFinanceMethods = useCallback(async () => {
        if (!roofCalc.roofingPartner || !roofCalc.RoofProduct) return;
        const roofState = String(roofCalc.roofState || 'CT').trim();
        const productCode = getRoofProductCode(roofCalc.FinanceProduct);
        const rpParts = String(roofCalc.RoofProduct || '').trim().split(/\s+/);
        const ManufacturerName = rpParts[0] ?? '';
        const ProductName = rpParts.slice(1).join(' ') || '';
        incLoading();
        try {
            const out = (await masterServices.cascadingDropDown({
                ExcelFileName: 'Roofing Calculator',
                SheetName: 'Roof Pricing',
                InputValues: [
                    { CellNumber: 'C3', CellValue: roofState, Type: 'string' },
                    { CellNumber: 'C4', CellValue: productCode, Type: 'string' },
                    { CellNumber: 'C5', CellValue: 'Roofing', Type: 'string' },
                    { CellNumber: 'C6', CellValue: ManufacturerName, Type: 'string' },
                    { CellNumber: 'C7', CellValue: ProductName, Type: 'string' },
                    { CellNumber: 'R3', CellValue: String(roofCalc.roofingPartner), Type: 'string' },
                ],
                OutputCells: ['T4', 'T5'],
            })) as CascadingOutputValue[] | null;

            const methods = String(out?.[0]?.CellValue ?? '')
                .split('|')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((m) => ({ Text: m, Value: m }));
            setRoofingMethods(methods);

            const terms = String(out?.[1]?.CellValue ?? '')
                .split('|')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((t) => ({ Text: t, Value: t }));
            setRoofingTerms(terms);

            setRoofCalc((s) => {
                const rawM = s.FinancingMethod != null ? String(s.FinancingMethod).trim() : '';
                const rawT = s.FinancingTerm != null ? String(s.FinancingTerm).trim() : '';
                const nextMethod = rawM ? pickMatchingDropdownValue(rawM, methods) : null;
                const matchTerm = rawT ? pickMatchingDropdownValue(rawT, terms) : null;
                const nextTerm = matchTerm ?? (terms.length > 0 ? String(terms[0].Value ?? '') : null);
                return {
                    ...s,
                    FinancingMethod: nextMethod,
                    FinancingTerm: nextTerm,
                };
            });
        } finally {
            decLoading();
        }
    }, [roofCalc.roofState, roofCalc.FinanceProduct, roofCalc.RoofProduct, roofCalc.roofingPartner, getRoofProductCode, incLoading, decLoading]);

    useEffect(() => {
        if (!showRoof) return;
        void loadRoofProductsAndPartners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRoof, roofCalc.roofState, roofCalc.FinanceProduct]);

    useEffect(() => {
        if (!showRoof) return;
        void loadRoofFinanceMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRoof, roofCalc.roofingPartner, roofCalc.RoofProduct]);

    const updateRoofAdderTotals = useCallback(async (adders: RoofAdder[]) => {
        // Mirrors Angular `updateAdderData`: writes F/G/H rows and reads J totals.
        const inputValues: any[] = [];
        const outputCells: string[] = [];
        let i = 4;
        adders.forEach((item) => {
            inputValues.push({ CellNumber: `F${i}`, CellValue: item.default ? item.type : 'other', Type: 'string' });
            inputValues.push({
                CellNumber: `G${i}`,
                CellValue: item.type === 'RRR' ? String(item.quantity / 1000) : String(item.quantity),
                Type: 'string',
            });
            if (!item.default || item.type === 'RRR') {
                inputValues.push({ CellNumber: `H${i}`, CellValue: String(item.unitAmount), Type: 'string' });
            }
            outputCells.push(`J${i}`);
            i++;
        });
        outputCells.push('C7');

        incLoading();
        try {
            const out = (await masterServices.cascadingDropDown({
                ExcelFileName: 'Roofing Calculator',
                SheetName: 'Adder Pricing',
                InputValues: inputValues,
                OutputCells: outputCells,
            })) as CascadingOutputValue[] | null;

            setRoofCalc((s) => {
                const nextAdders = s.Adders.map((a, idx) => ({
                    ...a,
                    totalAmount: toNumber(getOut(`J${idx + 4}`, out), 0),
                }));
                return { ...s, Adders: nextAdders };
            });
        } finally {
            decLoading();
        }
    }, [incLoading, decLoading]);

    const roofAdderInputsKey = useMemo(
        () =>
            roofCalc.Adders.map((a) => `${a.default ? 1 : 0}|${String(a.type)}|${toNumber(a.quantity, 0)}|${toNumber(a.unitAmount, 0)}`).join('~'),
        [roofCalc.Adders],
    );

    useEffect(() => {
        if (!showRoof) return;
        const handle = window.setTimeout(() => {
            void updateRoofAdderTotals(roofCalc.Adders);
        }, 250);
        return () => window.clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRoof, roofAdderInputsKey, updateRoofAdderTotals]);

    const calculateRoof = useCallback(async () => {
        const totalAdders = roofCalc.Adders.reduce((sum, a) => sum + toNumber(a.totalAmount, 0), 0);
        const roofState = String(roofCalc.roofState || 'CT').trim();
        const productCode = getRoofProductCode(roofCalc.FinanceProduct);
        const parts = String(roofCalc.RoofProduct || '').trim().split(/\s+/);
        const ManufacturerName = parts[0] ?? '';
        const ProductName = parts.slice(1).join(' ') || '';
        incLoading();
        try {
            const out = (await masterServices.cascadingDropDown({
                ExcelFileName: 'Roofing Calculator',
                SheetName: 'Roof Pricing',
                InputValues: [
                    { CellNumber: 'C3', CellValue: roofState, Type: 'string' },
                    { CellNumber: 'C4', CellValue: productCode, Type: 'string' },
                    { CellNumber: 'C5', CellValue: roofCalc.Roofing_Team === null ? 'Solar' : 'Roofing', Type: 'string' },
                    { CellNumber: 'C6', CellValue: ManufacturerName, Type: 'string' },
                    { CellNumber: 'C7', CellValue: ProductName, Type: 'string' },
                    { CellNumber: 'C8', CellValue: String(roofCalc.LowSlope ?? 0), Type: 'string' },
                    { CellNumber: 'C9', CellValue: String(roofCalc.NormalSlope ?? 0), Type: 'string' },
                    { CellNumber: 'H3', CellValue: '', Type: 'string' },
                    { CellNumber: 'H4', CellValue: String(roofCalc.GrossRoofingPrice ?? 0), Type: 'string' },
                    { CellNumber: 'H5', CellValue: String(roofCalc.Deposit ?? 0), Type: 'string' },
                    { CellNumber: 'H6', CellValue: roofCalc.SDDF_Lead_source ?? '', Type: 'string' },
                    { CellNumber: 'H7', CellValue: roofCalc.Sales_Division ?? '', Type: 'string' },
                    { CellNumber: 'M3', CellValue: String(totalAdders), Type: 'string' },
                    { CellNumber: 'H9', CellValue: String((roofCalc.PricePerSqFeet ?? 0) + 0.7), Type: 'string' },
                    { CellNumber: 'H10', CellValue: String(roofCalc.PricePerSqFeet ?? 0), Type: 'string' },
                    { CellNumber: 'R3', CellValue: roofCalc.roofingPartner ?? '', Type: 'string' },
                    { CellNumber: 'R5', CellValue: roofCalc.FinancingMethod ?? '', Type: 'string' },
                    { CellNumber: 'R6', CellValue: roofCalc.FinancingTerm ?? 'null', Type: 'string' },
                ],
                OutputCells: ['W3', 'W4', 'W5', 'W6', 'W7','W9','W10'],
            })) as CascadingOutputValue[] | null;

            const DealerFee = excelTruthyNumber(getOut('W6', out));
            const GrossSalesPrice = excelTruthyNumber(getOut('W3', out));
            const MonthlyFinancePayment = excelTruthyNumber(getOut('W5', out));
            const SalesCommission = excelTruthyNumber(getOut('W4', out));
            // const GrossSalesPricePerSquareFoot = excelTruthyNumber(getOut('W9', out));
            // const NetSalesPricePerSquareFoot = excelTruthyNumber(getOut('W10', out));

            let GrossSalesPricePerSquareFoot = (excelTruthyNumber(getOut('W9', out))) ? excelTruthyNumber(getOut('W9', out)) : 0;
            let NetSalesPricePerSquareFoot = (excelTruthyNumber(getOut('W10', out))) ? excelTruthyNumber(getOut('W10', out)) : 0;

            setRoofCalc((s) => ({
                ...s,
                ouput: {
                    DealerFee,
                    GrossSalesPricePerSquareFoot,
                    GrossSalesPrice,
                    MonthlyFinancePayment,
                    NetSalesPricePerSquareFoot,
                    SalesCommission,
                    MinimumSolarSystemSalesPrice: 0,
                },
            }));
        } finally {
            decLoading();
        }
    }, [roofCalc, getRoofProductCode, incLoading, decLoading]);

    const [batteryCalc, setBatteryCalc] = useState<BatteryCalcState>(() => createBatteryCalcInitial(priceQuote, opportunityDetails));

    const [batteryPartners, setBatteryPartners] = useState<Array<{ Text: string; Value: string | number }>>([]);
    const [batteryMethods, setBatteryMethods] = useState<Array<{ Text: string; Value: string }>>([]);
    const [batteryTermAprArr, setBatteryTermAprArr] = useState<Array<{ Text: string; Value: string }>>([]);
    const [batteryManufacturers, setBatteryManufacturers] = useState<Array<{ Text: string; Value: string }>>([]);
    const [batteryQuantities, setBatteryQuantities] = useState<Array<{ Text: string; Value: string }>>([]);
    const [batteryProducts, setBatteryProducts] = useState<Array<{ Text: string; Value: string }>>([]);

    const batteryCalcRef = useRef(batteryCalc);
    batteryCalcRef.current = batteryCalc;

    const filteredBatteryPartners = useMemo(() => {
        let list = (financePartnersList || []).filter((item: any) => parseInt(String(item.Value), 10) !== SERVICE_FINANCE_PARTNER_ID);
        const stateRow = stateList.find((item: any) => item.Value === batteryCalc.batteryState);
        const isOhio = String(stateRow?.Text ?? '')
            .trim()
            .toLowerCase() === 'ohio';
        if (isOhio) {
            list = list.filter((item: any) => item.Text !== COMMON_CONSTANTS.GoodLeapPartnerName);
        }
        return list.map((p: any) => ({ Text: String(p.Text ?? ''), Value: p.Value }));
    }, [financePartnersList, stateList, batteryCalc.batteryState]);

    useEffect(() => {
        if (!showBattery) return;
        setBatteryPartners(filteredBatteryPartners);
    }, [showBattery, filteredBatteryPartners]);

    const getBatteryProductNotation = useCallback(
        (batteryFinancedDisplayName: string | null): 'B' | 'SB' => {
            const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest ?? {};
            const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
            let product: 'B' | 'SB' = 'B';
            if (interest.Solar === true) {
                const solarPartner = (Array.isArray(products) ? products : []).find((o: any) => o.Product_Type_Name === 'Solar');
                if (solarPartner && String(solarPartner.Finance_Partner_Display_Name) === String(batteryFinancedDisplayName)) {
                    product = 'SB';
                }
            }
            return product;
        },
        [opportunityDetails],
    );

    const getBatteryFinancePartnerId = useCallback(
        (batteryFinancedDisplayName: string | null) => {
            const row = (financePartnersList || []).find((o: any) => o.Text === batteryFinancedDisplayName);
            return row?.Value ?? 0;
        },
        [financePartnersList],
    );

    const loadBatteryTermAndAprInternal = useCallback(
        async (financingMethodOverride?: string | null) => {
            const bc = batteryCalcRef.current;
            const fm = financingMethodOverride ?? bc.FinancingMethod;
            if (!bc.batteryFinanced || !fm) return;
            const st = String(bc.batteryState || 'CT').trim();
            incLoading();
            try {
                const out = (await masterServices.cascadingDropDown({
                    ExcelFileName: 'Sunnova Financial Data',
                    SheetName: 'SVA Financing Options',
                    InputValues: [
                        { Type: 'String', CellNumber: 'B4', CellValue: st },
                        { Type: 'String', CellNumber: 'C4', CellValue: 'B' },
                        { Type: 'String', CellNumber: 'D4', CellValue: String(bc.batteryFinanced) },
                        { Type: 'String', CellNumber: 'F4', CellValue: String(fm) },
                    ],
                    OutputCells: ['N4'],
                })) as CascadingOutputValue[] | null;

                const raw = String(out?.[0]?.CellValue ?? '').trim();
                const terms = raw
                    ? raw
                          .split('|')
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .map((t) => ({ Text: t, Value: t }))
                    : [];
                setBatteryTermAprArr(terms);
                setBatteryCalc((s) => {
                    const match = s.termAPR ? terms.find((o) => String(o.Value ?? '').trim() === String(s.termAPR).trim()) : null;
                    const nextTerm = match ? String(match.Value ?? '') : (terms.length > 0 ? String(terms[0].Value ?? '') : null);
                    return { ...s, termAPR: nextTerm };
                });
            } finally {
                decLoading();
            }
        },
        [incLoading, decLoading],
    );

    const loadBatteryFinanceMethods = useCallback(async () => {
        const bc = batteryCalcRef.current;
        if (!bc.batteryFinanced) return;
        const st = String(bc.batteryState || 'CT').trim();
        let product: 'B' | 'SB' = 'B';
        const solarPartner: any = null;
        const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest ?? {};
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        if (interest.Solar === true) {
            const sp = (Array.isArray(products) ? products : []).find((o: any) => o.Product_Type_Name === 'Solar');
            product = sp && String(sp.Finance_Partner_Display_Name) === String(bc.batteryFinanced) ? 'SB' : 'B';
        }
        const prevMethod = bc.FinancingMethod;
        incLoading();
        try {
            let methods: { Text: string; Value: string }[] = [];
            if (
                product === 'B' ||
                solarPartner?.Finance_Method_Name === 'Lease Storage' ||
                solarPartner?.Finance_Method_Name === 'Loan Storage' ||
                solarPartner?.Finance_Method_Name === 'PPA Storage'
            ) {
                const out = (await masterServices.cascadingDropDown({
                    ExcelFileName: 'Sunnova Financial Data',
                    SheetName: 'SVA Financing Options',
                    InputValues: [
                        { Type: 'String', CellNumber: 'B4', CellValue: st },
                        { Type: 'String', CellNumber: 'C4', CellValue: product },
                        { Type: 'String', CellNumber: 'D4', CellValue: String(bc.batteryFinanced) },
                        { Type: 'String', CellNumber: 'C8', CellValue: String(priceQuote?.EligibleProduct ?? '') },
                        { Type: 'String', CellNumber: 'E8', CellValue: String(priceQuote?.ExcelUtilityCompanyName ?? '') },
                    ],
                    OutputCells: ['E4'],
                })) as CascadingOutputValue[] | null;

                const batteryMethords = String(out?.[0]?.CellValue ?? '')
                    .split('|')
                    .map((s) => s.trim())
                    .filter(Boolean);
                methods = [...batteryMethords].reverse().map((m) => ({ Text: m, Value: m }));
            } else {
                const batteryFinanceMethodName = String(priceQuote?.Battery?.Finance_Method_Name ?? '').trim();
                methods = batteryFinanceMethodName ? [{ Text: batteryFinanceMethodName, Value: batteryFinanceMethodName }] : [];
            }
            setBatteryMethods(methods);
            const methodOk = !prevMethod || methods.findIndex((o) => o.Value === prevMethod) !== -1;
            let nextMethod = methodOk ? prevMethod : null;
            
            // To mimic Angular's battery detail form which auto-selects the first option
            // before the calculator opens, we must do the same if there's no match here.
            if (!methodOk && methods.length > 0) {
                // If the user selected "Cash", try to match "Cash Contract" fuzzily,
                // otherwise just pick the first available option.
                const isCash = String(bc.batteryFinanced).trim().toLowerCase() === 'cash';
                const cashContract = methods.find(m => m.Value.toLowerCase().includes('cash'));
                if (isCash && cashContract) {
                    nextMethod = cashContract.Value;
                } else {
                    nextMethod = methods[0].Value;
                }
            }

            setBatteryCalc((s) => ({ ...s, FinancingMethod: nextMethod }));
            if (nextMethod) {
                await loadBatteryTermAndAprInternal(nextMethod);
            } else {
                setBatteryTermAprArr([]);
            }
        } finally {
            decLoading();
        }
    }, [opportunityDetails, priceQuote, incLoading, decLoading, loadBatteryTermAndAprInternal]);

    const loadBatteryManufacturersInitial = useCallback(async () => {
        const bc = batteryCalcRef.current;
        const st = String(bc.batteryState || 'CT').trim();
        incLoading();
        try {
            const out = (await masterServices.cascadingDropDown({
                ExcelFileName: 'Battery Inverter Calculator',
                SheetName: 'Inverter Configurator',
                InputValues: [
                    { CellNumber: 'B3', CellValue: st, Type: 'string' },
                    { CellNumber: 'C3', CellValue: 'B', Type: 'string' },
                    { CellNumber: 'D9', CellValue: String(batteryCalcRef.current.batteryFinanced ?? ''), Type: 'string' },
                    { CellNumber: 'E9', CellValue: String(batteryCalcRef.current.FinancingMethod ?? ''), Type: 'string' },
                ],
                OutputCells: ['D6', 'F6'],
            })) as CascadingOutputValue[] | null;

            const mans = String(out?.[0]?.CellValue ?? '')
                .split('|')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((m) => ({ Text: m, Value: m }));
            setBatteryManufacturers(mans);

            const qtyRaw = String(out?.[1]?.CellValue ?? '').trim();
            const qty = qtyRaw
                ? qtyRaw
                      .split('|')
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((q) => ({ Text: q, Value: q }))
                : [];
            setBatteryQuantities(qty);

            const cur = batteryCalcRef.current.batteryManufacturer;
            if (cur != null && mans.findIndex((o) => o.Value === cur || o.Text === cur) === -1) {
                setBatteryCalc((s) => ({ ...s, batteryManufacturer: null, BatteryProduct: null }));
            } else if (cur) {
                const fpId = getBatteryFinancePartnerId(batteryCalcRef.current.batteryFinanced);
                incLoading();
                try {
                    const res = await designServices.getBatteryModel(cur, fpId);
                    const arr = res?.data ?? res;
                    if (Array.isArray(arr)) {
                        setBatteryProducts(
                            arr.map((x: any) => ({
                                Text: String(x.Text ?? x.text ?? ''),
                                Value: String(x.Value ?? x.value ?? x.Text ?? x.text ?? ''),
                            })),
                        );
                    } else {
                        setBatteryProducts([]);
                    }
                } catch {
                    setBatteryProducts([]);
                } finally {
                    decLoading();
                }

                const prod = getBatteryProductNotation(batteryCalcRef.current.batteryFinanced);
                incLoading();
                try {
                    const out2 = (await masterServices.cascadingDropDown({
                        ExcelFileName: 'Battery Inverter Calculator',
                        SheetName: 'Inverter Configurator',
                        InputValues: [
                            { CellNumber: 'B3', CellValue: st, Type: 'string' },
                            { CellNumber: 'C3', CellValue: prod, Type: 'string' },
                            { CellNumber: 'F3', CellValue: cur, Type: 'string' },
                            { CellNumber: 'D9', CellValue: String(batteryCalcRef.current.batteryFinanced ?? ''), Type: 'string' },
                            { CellNumber: 'E9', CellValue: String(batteryCalcRef.current.FinancingMethod ?? ''), Type: 'string' },
                        ],
                        OutputCells: ['F6'],
                    })) as CascadingOutputValue[] | null;
                    const q2 = String(out2?.[0]?.CellValue ?? '')
                        .trim()
                        .split('|')
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map((q) => ({ Text: q, Value: q }));
                    setBatteryQuantities(q2);
                } finally {
                    decLoading();
                }
            }
        } finally {
            decLoading();
        }
    }, [getBatteryFinancePartnerId, getBatteryProductNotation, incLoading, decLoading]);

    const refreshBatteryQuantitiesForManufacturer = useCallback(
        async (manufacturer: string) => {
            const bc = batteryCalcRef.current;
            const st = String(bc.batteryState || 'CT').trim();
            const prod = getBatteryProductNotation(bc.batteryFinanced);
            incLoading();
            try {
                const out2 = (await masterServices.cascadingDropDown({
                    ExcelFileName: 'Battery Inverter Calculator',
                    SheetName: 'Inverter Configurator',
                    InputValues: [
                        { CellNumber: 'B3', CellValue: st, Type: 'string' },
                        { CellNumber: 'C3', CellValue: prod, Type: 'string' },
                        { CellNumber: 'F3', CellValue: manufacturer, Type: 'string' },
                        { CellNumber: 'D9', CellValue: String(batteryCalcRef.current.batteryFinanced ?? ''), Type: 'string' },
                        { CellNumber: 'E9', CellValue: String(batteryCalcRef.current.FinancingMethod ?? ''), Type: 'string' },
                    ],
                    OutputCells: ['F6'],
                })) as CascadingOutputValue[] | null;
                const q2 = String(out2?.[0]?.CellValue ?? '')
                    .trim()
                    .split('|')
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((q) => ({ Text: q, Value: q }));
                setBatteryQuantities(q2);
            } finally {
                decLoading();
            }
        },
        [getBatteryProductNotation, incLoading, decLoading],
    );

    const loadBatteryProductsFromApi = useCallback(
        async (manufacturer: string, financedDisplayName: string | null) => {
            const fpId = getBatteryFinancePartnerId(financedDisplayName);
            incLoading();
            try {
                const res = await designServices.getBatteryModel(manufacturer, fpId);
                const arr = res?.data ?? res;
                if (Array.isArray(arr)) {
                    setBatteryProducts(
                        arr.map((x: any) => ({
                            Text: String(x.Text ?? x.text ?? ''),
                            Value: String(x.Value ?? x.value ?? x.Text ?? x.text ?? ''),
                        })),
                    );
                } else {
                    setBatteryProducts([]);
                }
            } catch {
                setBatteryProducts([]);
            } finally {
                decLoading();
            }
        },
        [getBatteryFinancePartnerId, incLoading, decLoading],
    );

    useEffect(() => {
        if (!showBattery) return;
        void (async () => {
            await loadBatteryManufacturersInitial();
            if (batteryCalcRef.current.batteryFinanced) {
                await loadBatteryFinanceMethods();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showBattery, batteryCalc.batteryState, batteryCalc.batteryFinanced]);

    const calculateBattery = useCallback(async () => {
        const bc = batteryCalcRef.current;
        const st = String(bc.batteryState || 'CT').trim();
        const product = getBatteryProductNotation(bc.batteryFinanced);
        incLoading();
        try {
            const out = (await masterServices.cascadingDropDown({
                ExcelFileName: 'Battery Calculator',
                SheetName: 'Battery IO',
                InputValues: [
                    { CellNumber: 'C3', CellValue: st, Type: 'string' },
                    { CellNumber: 'C4', CellValue: product, Type: 'string' },
                    { CellNumber: 'C5', CellValue: String(bc.batteryManufacturer ?? ''), Type: 'string' },
                    { CellNumber: 'C6', CellValue: String(bc.batteryCount ?? ''), Type: 'string' },
                    { CellNumber: 'C7', CellValue: String(bc.batteryFinanced ?? ''), Type: 'string' },
                    { CellNumber: 'C8', CellValue: String(bc.FinancingMethod ?? ''), Type: 'string' },
                    { CellNumber: 'C9', CellValue: String(bc.termAPR ?? ''), Type: 'string' },
                    { CellNumber: 'C10', CellValue: String(bc.CommissionTier ?? 'Standard'), Type: 'string' },
                    { CellNumber: 'C11', CellValue: bc.batteryFireEnclosure ? 'Yes' : 'No', Type: 'string' },
                    { CellNumber: 'C12', CellValue: bc.luminSmartPanel ? 'Yes' : 'No', Type: 'string' },
                    { CellNumber: 'C13', CellValue: String(bc.ancillaryAdderCost ?? 0), Type: 'string' },
                ],
                OutputCells: ['F4', 'F5', 'F6', 'F7'],
            })) as CascadingOutputValue[] | null;

            const DealerFee = excelTruthyNumber(getOut('F4', out));
            const BatteryGrossSalesPrice = excelTruthyNumber(getOut('F5', out));
            const MonthlyFinancePaymentByCustomer = excelTruthyNumber(getOut('F6', out));
            const SalesCommission = excelTruthyNumber(getOut('F7', out));

            setBatteryCalc((s) => ({
                ...s,
                ouput: { DealerFee, BatteryGrossSalesPrice, MonthlyFinancePaymentByCustomer, SalesCommission },
            }));
        } finally {
            decLoading();
        }
    }, [getBatteryProductNotation, incLoading, decLoading]);

    const roofTab = (
        <div className="calculator-wrapper">
            <div className="calculator-panel">
                <div className="panel-header">Customer Information</div>
                <div className="panel-body">
                    <div className="calc-grid-2">
                        <div className="calc-field">
                            <div className="calc-meta">
                                <span>Date</span>
                                <span className="calc-meta__value">{roofCalc.currentDate}</span>
                            </div>
                            <Input value={roofCalc.currentDate} disabled className="obd-contact-modal__input" />
                        </div>
                        <div className="calc-field">
                            <div className="calc-meta">
                                <span>Customer</span>
                                <span className="calc-meta__value">{roofCalc.customerName || '—'}</span>
                            </div>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={roofCalc.roofState}
                                options={stateList.map((s) => ({ value: s.Value, label: s.Text }))}
                                onChange={(v) => setRoofCalc((s) => ({ ...s, roofState: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                                placeholder={stateList.length === 0 ? 'Loading states…' : undefined}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="calculator-panel">
                <div className="panel-header">Roof Information</div>
                <div className="panel-body">
                    <div className="calc-grid-2">
                        <div className="calc-field">
                            <label>Existing Roof Type</label>
                            <Input
                                value={roofCalc.roofingType}
                                onChange={(e) => setRoofCalc((s) => ({ ...s, roofingType: e.target.value }))}
                                className="obd-contact-modal__input"
                            />

                            <label>Low Slope Squares</label>
                            <InputNumber value={roofCalc.LowSlope} min={0} precision={2} onChange={(v) => setRoofCalc((s) => ({ ...s, LowSlope: toNumber(v, 0) }))} />

                            <label># of Existing Shingle Layers</label>
                            <InputNumber value={roofCalc.ExistingShingleLayers} min={0} precision={0} onChange={(v) => setRoofCalc((s) => ({ ...s, ExistingShingleLayers: toNumber(v, 0) }))} />

                            <label>Finance Provider</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={roofCalc.roofingPartner ?? undefined}
                                options={roofingPartners.map((p) => ({ value: p.Value, label: p.Text }))}
                                onChange={(v) => setRoofCalc((s) => ({ ...s, roofingPartner: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            <label>Finance Product</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={roofCalc.FinanceProduct}
                                options={PRODUCT_LIST.map((p) => ({ value: p.Value, label: p.Text }))}
                                onChange={(v) => setRoofCalc((s) => ({ ...s, FinanceProduct: v as any }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />
                        </div>

                        <div className="calc-field">
                            <label>Standard Slope Squares</label>
                            <InputNumber value={roofCalc.NormalSlope} min={0} precision={2} onChange={(v) => setRoofCalc((s) => ({ ...s, NormalSlope: toNumber(v, 0) }))} />

                            <label>Total Roof Squares</label>
                            <Input value={String(roofCalc.TotalRoofSquares)} disabled className="obd-contact-modal__input" />

                            <label>Roofing Product</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={roofCalc.RoofProduct || undefined}
                                options={roofProductList.map((p) => ({ value: p, label: p }))}
                                onChange={(v) => setRoofCalc((s) => ({ ...s, RoofProduct: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            <label>Financing Method</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={roofCalc.FinancingMethod ?? undefined}
                                options={roofingMethods.map((m) => ({ value: m.Value, label: m.Text }))}
                                onChange={(v) => setRoofCalc((s) => ({ ...s, FinancingMethod: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            {roofingTerms.length > 0 && (
                                <>
                                    <label>Term and APR</label>
                                    <OBDSelect
                                        {...modalSelectDropdown}
                                        value={roofCalc.FinancingTerm ?? undefined}
                                        options={roofingTerms.map((t) => ({ value: t.Value, label: t.Text }))}
                                        onChange={(v) => setRoofCalc((s) => ({ ...s, FinancingTerm: v }))}
                                        className="obd-contact-modal__select"
                                        popupClassName={selectPopupClass}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <div className="calc-grid-2" style={{ marginTop: 12 }}>
                        <div className="calc-field">
                            <label>Gross Roofing Price ($/ft²)</label>
                            <InputNumber
                                value={roofCalc.PricePerSqFeet}
                                min={0}
                                precision={2}
                                formatter={usdAmountFormatter}
                                parser={usdAmountParser}
                                onFocus={selectAllAmountInputOnFocus}
                                onChange={(v) => setRoofCalc((s) => ({ ...s, PricePerSqFeet: toNumber(v, 0) }))}
                            />
                        </div>
                        <div className="calc-field">
                            <label>Deposit</label>
                            <InputNumber
                                value={roofCalc.Deposit}
                                min={0}
                                precision={2}
                                formatter={usdAmountFormatter}
                                parser={usdAmountParser}
                                onFocus={selectAllAmountInputOnFocus}
                                onChange={(v) => setRoofCalc((s) => ({ ...s, Deposit: toNumber(v, 0) }))}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="calculator-panel">
                <table className="table-panel">
                    <thead>
                        <tr>
                            <th>
                                <div className="adder-inputs-header">
                                    <span>Adder Inputs</span>
                                    <Button
                                        type="default"
                                        size="small"
                                        /* Icon-only: keeps vertical size predictable inside modal form styles. */
                                        icon={<PlusOutlined className="obd-roofing-calc-add-adder-plus" aria-hidden />}
                                        className="obd-roofing-calc-add-adder-btn"
                                        aria-label="Add adder row"
                                        onClick={() =>
                                            setRoofCalc((s) => ({
                                                ...s,
                                                Adders: [...s.Adders, { default: false, type: '', quantity: 0, unitAmount: 0, totalAmount: 0, canRemove: true }],
                                            }))
                                        }
                                    />
                                </div>
                            </th>
                            <th style={{ width: 110, textAlign: 'center' }}>Quantity</th>
                            <th style={{ width: 120, textAlign: 'right' }}>Unit Cost</th>
                            <th style={{ width: 120, textAlign: 'right' }}>Total Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roofCalc.Adders.map((a, idx) => (
                            <tr key={`${a.type}-${idx}`}>
                                <td>
                                    {a.default ? (
                                        <span>{a.type}</span>
                                    ) : (
                                        <div className="obd-roofing-calc-adder-type-combo">
                                            <input
                                                type="text"
                                                className="obd-roofing-calc-adder-type-field"
                                                value={a.type}
                                                placeholder="Other Adder Item"
                                                aria-label="Other adder item"
                                                onChange={(e) =>
                                                    setRoofCalc((s) => ({
                                                        ...s,
                                                        Adders: s.Adders.map((x, i) => (i === idx ? { ...x, type: e.target.value } : x)),
                                                    }))
                                                }
                                            />
                                            {a.canRemove ? (
                                                <button
                                                    type="button"
                                                    className="obd-roofing-calc-adder-remove"
                                                    aria-label="Remove adder row"
                                                    onClick={() =>
                                                        setRoofCalc((s) => ({ ...s, Adders: s.Adders.filter((_, i) => i !== idx) }))
                                                    }
                                                >
                                                    <DeleteOutlined aria-hidden />
                                                </button>
                                            ) : null}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <InputNumber
                                        value={a.quantity}
                                        min={0}
                                        precision={0}
                                        onChange={(v) =>
                                            setRoofCalc((s) => ({
                                                ...s,
                                                Adders: s.Adders.map((x, i) => (i === idx ? { ...x, quantity: toNumber(v, 0) } : x)),
                                            }))
                                        }
                                    />
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <InputNumber
                                        value={a.unitAmount}
                                        min={0}
                                        precision={2}
                                        disabled={a.default && a.type !== 'RRR'}
                                        formatter={usdAmountFormatter}
                                        parser={usdAmountParser}
                                        onFocus={selectAllAmountInputOnFocus}
                                        onChange={(v) =>
                                            setRoofCalc((s) => ({
                                                ...s,
                                                Adders: s.Adders.map((x, i) => (i === idx ? { ...x, unitAmount: toNumber(v, 0) } : x)),
                                            }))
                                        }
                                    />
                                </td>
                                <td style={{ textAlign: 'right' }}>{fmtCurrency(toNumber(a.totalAmount, 0))}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="calculator-panel">
                <div className="panel-header">
                    <span>Outputs</span>
                    <Button
                        type="primary"
                        size="small"
                        className="obd-roofing-calc-calculate-btn"
                        onClick={() => void calculateRoof()}
                        disabled={loadingCount > 0}
                    >
                        Calculate
                    </Button>
                </div>
                <div className="panel-body output-body">
                    <div className="out-label">Dealer Fee %</div>
                    <div className="out-val">{fmtPercent(roofCalc.ouput.DealerFee)}</div>
                    <div className="out-label">Gross Sales Price / Square Foot</div>
                    <div className="out-val">{fmtCurrency(roofCalc.ouput.GrossSalesPricePerSquareFoot)}</div>
                    <div className="out-label">Gross Sales Price</div>
                    <div className="out-val">{fmtCurrency(roofCalc.ouput.GrossSalesPrice)}</div>
                    <div className="out-label">Monthly Finance Payment</div>
                    <div className="out-val">{fmtCurrency(roofCalc.ouput.MonthlyFinancePayment)}</div>
                    <div className="out-label">Net Sales Price /Square Foot</div>
                    <div className="out-val">{fmtCurrency(toNumber(roofCalc.ouput.NetSalesPricePerSquareFoot, 0))}</div>
                    <div className="out-label">Sales Commission</div>
                    <div className="out-val">{fmtCurrency(roofCalc.ouput.SalesCommission)}</div>
                    <div className="out-label">Minimum Solar System Sales Price (if applicable)</div>
                    <div className="out-val">{fmtCurrency(roofCalc.ouput.MinimumSolarSystemSalesPrice)}</div>
                </div>
            </div>

            {/* Pricing Reference Table (Angular: after Outputs) */}
            <div className="pricing-ref">
                <div className="pricing-ref__note">
                    Pricing includes the following minimum quantities. The calculator will add anything above this minimum quantities to the overall Sales Price
                </div>
                <table className="pricing-ref__table">
                    <thead>
                        <tr>
                            <th>Adder</th>
                            <th>Price ($/each)</th>
                            <th className="pricing-ref__center">
                                Max Quantity with<br />No Adder Fee **
                            </th>
                            <th>Adder Explanation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Skylights (Fixed)</td>
                            <td>$ 2,000.00</td>
                            <td className="pricing-ref__center">0</td>
                            <td>None covered.</td>
                        </tr>
                        <tr>
                            <td>Skylights (Operable)</td>
                            <td>$ 2,000.00</td>
                            <td className="pricing-ref__center">0</td>
                            <td>None covered.</td>
                        </tr>
                        <tr>
                            <td>Skylight Curb Kits</td>
                            <td>$ 295.00</td>
                            <td className="pricing-ref__center">4</td>
                            <td>Up to 4 covered in pricing</td>
                        </tr>
                        <tr>
                            <td>Plywood</td>
                            <td>$ 100.00</td>
                            <td className="pricing-ref__center">5</td>
                            <td>Up to 5 covered in pricing</td>
                        </tr>
                        <tr>
                            <td>Existing Shingle Layers to be Removed</td>
                            <td>Quote</td>
                            <td className="pricing-ref__center">4</td>
                            <td>If over 4 layers, special quote</td>
                        </tr>
                        <tr>
                            <td>Chimney Flashing</td>
                            <td />
                            <td className="pricing-ref__center">N/A</td>
                            <td>100% covered in pricing</td>
                        </tr>
                        <tr>
                            <td colSpan={4}>Skylights Warranted for 10 years only</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="pricing-ref__footnotes">
                * Lead fees may apply
                <br />
                ** Adder charges start if maximum quantity is exceeded.
            </div>
        </div>
    );

    const batteryTab = (
        <div className="calculator-wrapper">
            <div className="calculator-panel">
                <div className="panel-header">Customer Information</div>
                <div className="panel-body">
                    <div className="calc-grid-2">
                        <div className="calc-field">
                            <div className="calc-meta">
                                <span>Date</span>
                                <span className="calc-meta__value">{batteryCalc.currentDate}</span>
                            </div>
                            <Input value={batteryCalc.currentDate} disabled className="obd-contact-modal__input" />
                        </div>
                        <div className="calc-field">
                            <div className="calc-meta">
                                <span>Customer</span>
                                <span className="calc-meta__value">{batteryCalc.customerName || '—'}</span>
                            </div>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={batteryCalc.batteryState}
                                options={stateList.map((s) => ({ value: s.Value, label: s.Text }))}
                                onChange={(v) => setBatteryCalc((s) => ({ ...s, batteryState: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                                placeholder={stateList.length === 0 ? 'Loading states…' : undefined}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="calculator-panel">
                <div className="panel-header">Battery Information</div>
                <div className="panel-body">
                    <div className="calc-grid-2">
                        <div className="calc-field">
                            <label>Battery Financed</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={batteryCalc.batteryFinanced ?? undefined}
                                options={batteryPartners.map((p) => ({ value: p.Text, label: p.Text }))}
                                onChange={(v) => setBatteryCalc((s) => ({ ...s, batteryFinanced: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            <label>Battery Manufacturer</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={batteryCalc.batteryManufacturer ?? undefined}
                                options={batteryManufacturers.map((m) => ({ value: m.Text, label: m.Text }))}
                                onChange={(v) => {
                                    setBatteryCalc((s) => ({ ...s, batteryManufacturer: v, BatteryProduct: null }));
                                    if (v) {
                                        void loadBatteryProductsFromApi(v, batteryCalcRef.current.batteryFinanced);
                                        void refreshBatteryQuantitiesForManufacturer(v);
                                    } else {
                                        setBatteryProducts([]);
                                        setBatteryQuantities([]);
                                    }
                                }}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            <label>Battery Count</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={batteryCalc.batteryCount}
                                options={(batteryQuantities.length ? batteryQuantities : [{ Text: '1', Value: '1' }]).map((q) => ({ value: q.Text, label: q.Text }))}
                                onChange={(v) => setBatteryCalc((s) => ({ ...s, batteryCount: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            <label>Pricing / Commission Tier</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={batteryCalc.CommissionTier}
                                options={['Floor', 'Min', 'Mid', 'Standard'].map((x) => ({ value: x, label: x }))}
                                onChange={(v) => setBatteryCalc((s) => ({ ...s, CommissionTier: v as any }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />
                        </div>

                        <div className="calc-field">
                            <label>Financing Method</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={batteryCalc.FinancingMethod ?? undefined}
                                options={batteryMethods.map((m) => ({ value: m.Text, label: m.Text }))}
                                onChange={(v) => {
                                    if (v) {
                                        setBatteryCalc((s) => ({ ...s, FinancingMethod: v }));
                                        void loadBatteryTermAndAprInternal(v);
                                    } else {
                                        setBatteryTermAprArr([]);
                                        setBatteryCalc((s) => ({ ...s, FinancingMethod: null, termAPR: null }));
                                    }
                                }}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            <label>Battery Product</label>
                            <OBDSelect
                                {...modalSelectDropdown}
                                value={batteryCalc.BatteryProduct ?? undefined}
                                options={batteryProducts.map((p) => ({ value: p.Text, label: p.Text }))}
                                onChange={(v) => setBatteryCalc((s) => ({ ...s, BatteryProduct: v }))}
                                className="obd-contact-modal__select"
                                popupClassName={selectPopupClass}
                            />

                            {batteryTermAprArr.length > 0 && (
                                <>
                                    <label>Term and APR</label>
                                    <OBDSelect
                                        {...modalSelectDropdown}
                                        value={batteryCalc.termAPR ?? undefined}
                                        options={batteryTermAprArr.map((t) => ({ value: t.Text, label: t.Text }))}
                                        onChange={(v) => setBatteryCalc((s) => ({ ...s, termAPR: v }))}
                                        className="obd-contact-modal__select"
                                        popupClassName={selectPopupClass}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="calculator-panel">
                <table className="table-panel">
                    <thead>
                        <tr>
                            <th colSpan={2}>Adder Inputs</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colSpan={2} style={{ padding: 0 }}>
                                <div style={{ padding: 12 }}>
                                    <div className="calc-grid-2">
                                        <div className="calc-field">
                                            <label>Battery Fire Enclosure (if not in garage)</label>
                                            <OBDSelect
                                                {...modalSelectDropdown}
                                                value={batteryCalc.batteryFireEnclosure}
                                                options={[
                                                    { value: true, label: 'Yes' },
                                                    { value: false, label: 'No' },
                                                ]}
                                                onChange={(v) => setBatteryCalc((s) => ({ ...s, batteryFireEnclosure: v }))}
                                                className="obd-contact-modal__select"
                                                popupClassName={selectPopupClass}
                                            />
                                        </div>
                                        <div className="calc-field">
                                            <label>Lumin Smart Panel</label>
                                            <OBDSelect
                                                {...modalSelectDropdown}
                                                value={batteryCalc.luminSmartPanel}
                                                options={[
                                                    { value: true, label: 'Yes' },
                                                    { value: false, label: 'No' },
                                                ]}
                                                onChange={(v) => setBatteryCalc((s) => ({ ...s, luminSmartPanel: v }))}
                                                className="obd-contact-modal__select"
                                                popupClassName={selectPopupClass}
                                            />
                                        </div>
                                        <div className="calc-field">
                                            <label>Lockout Panel</label>
                                            <OBDSelect
                                                {...modalSelectDropdown}
                                                value={batteryCalc.lockoutPanel}
                                                options={[
                                                    { value: true, label: 'Yes' },
                                                    { value: false, label: 'No' },
                                                ]}
                                                onChange={(v) => setBatteryCalc((s) => ({ ...s, lockoutPanel: v }))}
                                                className="obd-contact-modal__select"
                                                popupClassName={selectPopupClass}
                                            />
                                        </div>
                                        <div className="calc-field">
                                            <label>Ancillary Adder Cost</label>
                                            <InputNumber
                                                value={batteryCalc.ancillaryAdderCost}
                                                min={0}
                                                precision={2}
                                                formatter={usdAmountFormatter}
                                                parser={usdAmountParser}
                                                onFocus={selectAllAmountInputOnFocus}
                                                onChange={(v) => setBatteryCalc((s) => ({ ...s, ancillaryAdderCost: toNumber(v, 0) }))}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="calculator-panel">
                <div className="panel-header">
                    <span>Outputs</span>
                    <Button
                        type="primary"
                        size="small"
                        className="obd-roofing-calc-calculate-btn"
                        onClick={() => void calculateBattery()}
                        disabled={loadingCount > 0}
                    >
                        Calculate
                    </Button>
                </div>
                <div className="panel-body output-body">
                    <div className="out-label">Dealer Fee %</div>
                    <div className="out-val">{fmtPercent(batteryCalc.ouput.DealerFee)}</div>
                    <div className="out-label">Battery Gross Sales Price</div>
                    <div className="out-val">{fmtCurrency(batteryCalc.ouput.BatteryGrossSalesPrice)}</div>
                    <div className="out-label">Monthly Finance Payment by Customer*</div>
                    <div className="out-val">{fmtCurrency(batteryCalc.ouput.MonthlyFinancePaymentByCustomer)}</div>
                    <div className="out-label">Sales Commission*</div>
                    <div className="out-val">{fmtCurrency(batteryCalc.ouput.SalesCommission)}</div>
                </div>
                <div className="panel-body" style={{ paddingTop: 0, fontSize: 12, opacity: 0.75 }}>
                    *estimated amount after ITC (if applicable), except BatteryRetrofit
                </div>
            </div>
        </div>
    );

    const tabs: TabsProps['items'] = [
        showRoof ? { key: 'roof', label: 'Roof', children: roofTab } : null,
        showBattery ? { key: 'battery', label: 'Battery', children: batteryTab } : null,
    ].filter(Boolean) as TabsProps['items'];

    return (
        <div className={`obd-roofing-calc-host obd-theme-${theme} ${loadingCount > 0 ? 'obd-roofing-calc-host--loading' : ''}`}>
            <div className={`obd-roofing-calc obd-theme-${theme} ${loadingCount > 0 ? 'obd-roofing-calc--loading' : ''}`}>
                <Tabs
                    className="obd-roofing-calc-tabs"
                    defaultActiveKey={calculatorDefaultTab}
                    items={tabs}
                    indicator={{ size: 36, align: 'center' }}
                />
            </div>
            {loadingCount > 0 ? (
                <div
                    className="obd-roofing-calc-busy-overlay"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    aria-label="Loading"
                >
                    <Spin size="default" />
                    <span className="obd-roofing-calc-busy-overlay__label">Loading…</span>
                </div>
            ) : null}
        </div>
    );
};

export default RoofingCalculatorModal;

